import type { FileImageUsage } from '@vedalib/editor/lib/files'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import type { ImgHTMLAttributes } from 'react'
import React, { useMemo, useState } from 'react'

import { EMPTY_OBJECT, preventDefault } from 'constants/commonConstans'
import { useUrlByUsage } from 'utils/files'

import * as s from './ResponsiveImage.module.scss'

interface IResponsiveImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onLoad' | 'alt' | 'aria-label'> {
  usage: Omit<FileImageUsage, 'type'> | null
  overlay?: string | null
}

const ResponsiveImage = React.forwardRef<HTMLImageElement, IResponsiveImageProps>(
  ({ usage, overlay, ...rest }, ref) => {
    const src = useUrlByUsage(usage, FileUsageTypeEnum.image)
    const [loading, setLoading] = useState(true)
    const imgWidth = '100%'
    const imgHeight = '100%'
    const img = useMemo(
      () =>
        loading ? { ...rest.style, position: 'absolute', inset: 0 } : rest.style || EMPTY_OBJECT,
      [loading, rest.style],
    )
    const handleLoad = () => setLoading(false)

    return (
      <>
        {src && (
          <img
            {...rest}
            alt={usage?.accessibility || 'image'}
            aria-label={usage?.accessibility}
            className={s.img}
            onDragStart={preventDefault}
            onLoad={handleLoad}
            ref={ref}
            src={src}
            style={img}
          />
        )}

        <div
          className={s.mask}
          style={{
            backgroundColor: overlay || undefined,
            borderRadius: img.borderRadius,
            width: imgWidth,
            height: imgHeight,
          }}
        />
      </>
    )
  },
)

ResponsiveImage.displayName = 'ResponsiveImage'

export default ResponsiveImage
